import { motion } from "framer-motion";
import { fadeUp, staggerChild, staggerContainer } from "../lib/motion";

const surface = [
  "Content performs",
  "Engagement is strong",
  "DMs come in",
  "Opportunities show up",
];

const reality = [
  "Patchwork offers",
  "Manual sales follow-up",
  "Unclear customer journeys",
  "Weak lead handling",
  "No real backend monetization strategy",
  "Delivery systems that strain under growth",
];

const friction = [
  "People aren't sure what to buy",
  "Demand doesn't convert cleanly",
  "Revenue stays inconsistent",
  "The creator becomes the bottleneck",
  "Growth creates more complexity, not more leverage",
];

export const Problem = () => {
  return (
    <section
      data-testid="problem-section"
      className="relative py-24 md:py-32 lg:py-44 bg-[#101116] border-y border-[#282930]"
    >
      <div className="px-6 md:px-12 lg:px-20 max-w-[1500px] mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          <div className="lg:col-span-5">
            <motion.div {...fadeUp} className="flex items-center gap-3 mb-6">
              <span className="eyebrow">02 — Problem</span>
              <span className="block w-8 h-px bg-[#282930]" />
            </motion.div>
            <motion.h2
              {...fadeUp}
              className="font-display text-3xl sm:text-4xl lg:text-[44px] leading-[1.08] tracking-tight"
            >
              From the outside, it looks like growth. Behind the scenes, it&rsquo;s
              often held{" "}
              <span className="italic text-[#C9A26B]">together.</span>
            </motion.h2>
          </div>

          <div className="lg:col-span-7 lg:pl-12 lg:border-l lg:border-[#282930] space-y-12">
            <div>
              <p className="eyebrow text-[#A8A39D] mb-6">To the audience</p>
              <motion.ul {...staggerContainer} className="grid sm:grid-cols-2 gap-x-8 gap-y-3">
                {surface.map((s) => (
                  <motion.li
                    {...staggerChild}
                    key={s}
                    className="flex items-baseline gap-3 text-[#F4EFE6]"
                  >
                    <span className="text-[#C9A26B] font-display text-xs">&mdash;</span>
                    {s}
                  </motion.li>
                ))}
              </motion.ul>
            </div>

            <div>
              <p className="eyebrow text-[#A8A39D] mb-6">Behind the scenes</p>
              <motion.ul {...staggerContainer} className="space-y-2">
                {reality.map((s) => (
                  <motion.li
                    {...staggerChild}
                    key={s}
                    className="py-3 border-b border-[#1D1E24] text-[#F4EFE6] flex items-center justify-between"
                  >
                    <span>{s}</span>
                    <span className="text-[#A8A39D] text-xs font-mono">·</span>
                  </motion.li>
                ))}
              </motion.ul>
            </div>

            <div>
              <p className="eyebrow text-[#A8A39D] mb-6">Friction everywhere</p>
              <motion.ul {...staggerContainer} className="grid sm:grid-cols-2 gap-x-8 gap-y-3">
                {friction.map((s) => (
                  <motion.li
                    {...staggerChild}
                    key={s}
                    className="text-[#A8A39D]"
                  >
                    {s}
                  </motion.li>
                ))}
              </motion.ul>
            </div>
          </div>
        </div>

        <motion.blockquote
          {...fadeUp}
          className="mt-24 lg:mt-32 max-w-4xl mx-auto text-center"
        >
          <p className="font-display text-2xl md:text-3xl lg:text-4xl leading-[1.2] text-[#F4EFE6] italic">
            &ldquo;A weak backend doesn&rsquo;t get fixed by more attention.
            <br />
            It gets exposed by it.&rdquo;
          </p>
          <span className="mt-8 inline-block w-12 h-px bg-[#C9A26B]" />
        </motion.blockquote>
      </div>
    </section>
  );
};

export default Problem;
